import { ESCALA, MARGENS_PADRAO } from "../tema";
import colunaAgrupada from "./colunaAgrupada";
import barraRanking from "./barraRanking";
import cilindroRanking from "./cilindroRanking";
import linhaRotulada from "./linhaRotulada";
import linhaHistorica from "./linhaHistorica";
import colunaGradiente from "./colunaGradiente";
import barrasSeta from "./barrasSeta";
import funilComparativo from "./funilComparativo";
import roscaParticipacao from "./roscaParticipacao";
import areaEvolucao from "./areaEvolucao";
import colunaEmpilhada from "./colunaEmpilhada";
import colunaLinha from "./colunaLinha";
import variacaoColunas from "./variacaoColunas";

/**
 * Registro das famílias de gráfico.
 *
 * Cada modelo expõe o mesmo contrato:
 *   id         identificador salvo no spec e na coluna grf_modelo
 *   label      nome exibido no seletor
 *   descricao  uma linha explicando quando usar
 *   build(spec)   -> opções do Highcharts
 *   exemplo()     -> spec pronto, com os dados da imagem de referência
 *
 * Acrescentar uma família é criar um arquivo e somar uma linha aqui.
 */
/** Altura reservada para a linha de observação no rodapé. */
const ESPACO_RODAPE = 11 * ESCALA;

/** Espaçamento que o Highcharts usa quando a margem do lado é automática. */
const ESPACO_HIGHCHARTS = { spacingTop: 10, spacingRight: 10, spacingBottom: 15, spacingLeft: 10 };

const LADOS = [
  // topo nunca vira marginTop: com margem superior explícita o Highcharts
  // trata a legenda como flutuante e a desenha por cima das colunas (ver
  // o comentário em base.js). Só o spacingTop é seguro aqui.
  { chave: "topo", margem: null, espaco: "spacingTop" },
  { chave: "direita", margem: "marginRight", espaco: "spacingRight" },
  { chave: "baixo", margem: "marginBottom", espaco: "spacingBottom" },
  { chave: "esquerda", margem: "marginLeft", espaco: "spacingLeft" },
];

/**
 * Soma a área segura às margens que a família já definiu.
 *
 * Onde a família fixou uma margem, a folga entra nela; onde deixou o
 * Highcharts calcular (caso do eixo de categorias do ranking), entra no
 * spacing, que é o que sobra por fora do cálculo automático. Assim a folga
 * vale para todas as famílias sem desmanchar o que cada uma reserva.
 */
export function aplicarAreaSegura(opcoes, spec) {
  const margens = { ...MARGENS_PADRAO, ...(spec?.margens || {}) };
  const chart = { ...opcoes.chart };
  for (const { chave, margem, espaco } of LADOS) {
    const folga = Number(margens[chave]) || 0;
    if (!folga) continue;
    if (margem && typeof chart[margem] === "number") chart[margem] += folga;
    else chart[espaco] = (chart[espaco] ?? ESPACO_HIGHCHARTS[espaco]) + folga;
  }
  return { ...opcoes, chart };
}

export const MODELOS = [
  colunaAgrupada,
  colunaEmpilhada,
  colunaLinha,
  variacaoColunas,
  barraRanking,
  cilindroRanking,
  linhaRotulada,
  linhaHistorica,
  areaEvolucao,
  colunaGradiente,
  barrasSeta,
  roscaParticipacao,
  funilComparativo,
];

export const MODELOS_POR_ID = MODELOS.reduce((acc, modelo) => {
  acc[modelo.id] = modelo;
  return acc;
}, {});

export const opcoesModelo = MODELOS.map((m) => ({
  value: m.id,
  label: m.label,
  descricao: m.descricao,
}));

/** "12px" * fator -> "13px" (arredondado). */
const escalarFonte = (tamanho, fator) => {
  const n = parseFloat(tamanho);
  return Number.isFinite(n) ? `${Math.round(n * fator)}px` : tamanho;
};

/**
 * Ajustes da caixa de valor (o rótulo de cada ponto), à parte do resto do
 * gráfico: deslocamento vertical/horizontal e tamanho próprio do texto.
 * Vale para todas as famílias porque mexe nos dataLabels já montados —
 * tanto os de plotOptions quanto os de cada série.
 *
 *   rotulo.deslocY   px da imagem final; positivo SOBE a caixa
 *   rotulo.deslocX   px; positivo vai para a direita
 *   rotulo.tamanho   multiplica só o texto da caixa (1 = como está)
 */
function ajustarCaixaDeValor(opcoes, spec) {
  const r = spec?.rotulo || {};
  const dy = Number(r.deslocY) || 0;
  const dx = Number(r.deslocX) || 0;
  const fator = Number(r.tamanho) || 1;
  if (!dy && !dx && fator === 1) return;

  const ajustar = (dl) => {
    if (!dl || typeof dl !== "object") return dl;
    if (Array.isArray(dl)) return dl.map(ajustar);
    const novo = { ...dl };
    if (dy) novo.y = (Number(dl.y) || 0) - dy * ESCALA;
    if (dx) novo.x = (Number(dl.x) || 0) + dx * ESCALA;
    if (fator !== 1 && dl.style?.fontSize) novo.style = { ...dl.style, fontSize: escalarFonte(dl.style.fontSize, fator) };
    // Rosca: o rótulo fica fora da fatia, a "posição" é a distância.
    if (dy && typeof dl.distance === "number") novo.distance = dl.distance + dy * ESCALA;
    return novo;
  };

  if (opcoes.plotOptions) {
    opcoes.plotOptions = Object.fromEntries(
      Object.entries(opcoes.plotOptions).map(([tipo, o]) => [tipo, o?.dataLabels ? { ...o, dataLabels: ajustar(o.dataLabels) } : o])
    );
  }
  if (Array.isArray(opcoes.series)) {
    opcoes.series = opcoes.series.map((s) => (s?.dataLabels ? { ...s, dataLabels: ajustar(s.dataLabels) } : s));
  }
}

/**
 * Texto do eixo de categorias: tamanho e posição (sobe/desce), à parte.
 *   eixo.tamanhoCategoria   multiplica o texto das categorias
 *   eixo.deslocCategoria    px; positivo SOBE o texto
 */
function ajustarEixoCategorias(opcoes, spec) {
  const e = spec?.eixo || {};
  const fator = Number(e.tamanhoCategoria) || 1;
  const dy = Number(e.deslocCategoria) || 0;
  if (fator === 1 && !dy) return;
  const ajustar = (eixo) => {
    if (!eixo?.labels) return eixo;
    const labels = { ...eixo.labels };
    if (fator !== 1 && labels.style?.fontSize) labels.style = { ...labels.style, fontSize: escalarFonte(labels.style.fontSize, fator) };
    if (dy) labels.y = (Number(labels.y) || 0) - dy * ESCALA;
    return { ...eixo, labels };
  };
  opcoes.xAxis = Array.isArray(opcoes.xAxis) ? opcoes.xAxis.map(ajustar) : ajustar(opcoes.xAxis);
}

// Folga em volta dos dados, como fração da amplitude deles. Menos folga
// = a linha ocupa a altura toda e as variações aparecem mais; mais folga
// = a linha achata e as variações ficam suaves.
const FOLGA_POR_NIVEL = { "-3": 4, "-2": 1.5, "-1": 0.6, 1: 0.12, 2: 0.04, 3: 0.01 };
// Espaço acima do ponto mais alto, para a caixa de valor não colar no topo.
const FOLGA_ROTULO = 0.15;
const SEM_EIXO_DE_VALOR = new Set(["pie", "funnel", "pyramid", "variablepie"]);
// Famílias sem eixo de valores para ajustar (o funil desenha à mão).
const SEM_AMPLITUDE = new Set(["funil-comparativo", "rosca-participacao"]);

/**
 * "Variações": recalcula mínimo e máximo do eixo de valores a partir dos
 * dados, para acentuar (nível 1 a 3) ou suavizar (-1 a -3) o desenho.
 * Mínimo/máximo digitados à mão continuam valendo — só o lado vazio é
 * calculado. Gráfico empilhado fica de fora (o total não é a soma visível).
 */
function ajustarAmplitude(opcoes, spec) {
  const nivel = Math.max(-3, Math.min(3, Math.round(Number(spec?.eixo?.amplitude) || 0)));
  if (!nivel || SEM_AMPLITUDE.has(spec?.modelo)) return;
  const tipoPadrao = opcoes.chart?.type;
  const empilhado = Object.values(opcoes.plotOptions || {}).some((o) => o?.stacking);
  if (empilhado) return;

  const eixos = Array.isArray(opcoes.yAxis) ? opcoes.yAxis : [opcoes.yAxis || {}];
  const porEixo = eixos.map(() => ({ valores: [], colunas: false }));
  for (const s of opcoes.series || []) {
    const tipo = s?.type || tipoPadrao;
    if (SEM_EIXO_DE_VALOR.has(tipo)) continue;
    const i = typeof s.yAxis === "number" ? s.yAxis : 0;
    if (!porEixo[i]) continue;
    if (tipo === "column" || tipo === "bar" || tipo === "cylinder") porEixo[i].colunas = true;
    for (const p of s.data || []) {
      const v = typeof p === "number" ? p : p && typeof p === "object" ? p.y : null;
      if (typeof v === "number" && Number.isFinite(v)) porEixo[i].valores.push(v);
    }
  }

  const novos = eixos.map((eixo, i) => {
    const { valores, colunas } = porEixo[i];
    if (!valores.length) return eixo;
    const lo = Math.min(...valores);
    const hi = Math.max(...valores);
    const span = hi - lo || Math.abs(hi) * 0.1 || 1;

    // A família já fixou o intervalo (ex.: variação em colunas, simétrico
    // em torno do zero para queda e alta terem o mesmo peso): em vez de
    // trocar por outro, abre ou aperta ESSE intervalo em torno do centro.
    const manualMin = i === 0 && spec?.eixo?.min != null;
    const manualMax = i === 0 && spec?.eixo?.max != null;
    if (eixo?.min != null && eixo?.max != null && !manualMin && !manualMax) {
      const centro = (eixo.min + eixo.max) / 2;
      const meia = (eixo.max - eixo.min) / 2;
      const FATOR = { "-3": 4, "-2": 2.5, "-1": 1.6, 1: 0.85, 2: 0.7, 3: 0.55 };
      // Nunca apertar a ponto de cortar os dados e a caixa de valor.
      const precisa = Math.max(hi - centro, centro - lo) * (1 + FOLGA_ROTULO);
      const novaMeia = Math.max(meia * FATOR[nivel], precisa);
      return { ...eixo, min: centro - novaMeia, max: centro + novaMeia, startOnTick: false, endOnTick: false };
    }
    const folga = span * FOLGA_POR_NIVEL[nivel];
    let min = lo - folga;
    const max = hi + folga + span * FOLGA_ROTULO;
    // Coluna cresce a partir do zero: suavizar não pode empurrar o eixo
    // para baixo de zero quando os dados são todos positivos.
    if (colunas && lo >= 0 && min < 0) min = 0;
    // Com um dos lados digitado à mão, o outro tem de ficar do lado certo
    // dele (mínimo manual acima dos dados não pode gerar máximo menor).
    const minFinal = eixo?.min ?? min;
    let maxFinal = eixo?.max ?? max;
    if (maxFinal <= minFinal) maxFinal = minFinal + span * (1 + FOLGA_ROTULO);
    const minAjustado = eixo?.min == null && minFinal >= maxFinal ? maxFinal - span * (1 + FOLGA_ROTULO) : minFinal;
    return {
      ...eixo,
      min: minAjustado,
      max: maxFinal,
      // Sem isso o Highcharts arredonda o intervalo para o próximo "tick"
      // e desfaz parte do ajuste.
      startOnTick: false,
      endOnTick: false,
      minPadding: 0,
      maxPadding: 0,
    };
  });
  opcoes.yAxis = Array.isArray(opcoes.yAxis) ? novos : novos[0];
}

/**
 * "Posição do gráfico": sobe ou desce o desenho (linhas/colunas junto com
 * as caixas de valor) dentro da área do gráfico, sem mexer em título nem
 * categorias.
 *
 *   eixo.posicao   % da altura útil; positivo SOBE o desenho
 *
 * Desloca o próprio eixo de valores dentro da área (yAxis.top), mantendo a
 * altura: o desenho anda junto, com o intervalo exatamente como o
 * Highcharts calculou (automático, manual ou de "Variações") — nada é
 * estimado. Fica nas OPÇÕES, então vale na criação, no chart.update() da
 * pré-visualização e na exportação.
 *
 * O que tem BASE (coluna, cilindro, área) fica com a base presa no lugar
 * e só o topo sobe ou desce — a coluna estica ou encolhe. Transladar tudo
 * deixava as colunas flutuando (subindo) ou passando por cima das
 * categorias (descendo; o 3D não é recortado na borda). Linha não tem
 * base: anda inteira. Barra horizontal fica de fora (lá o eixo de valores
 * é o horizontal).
 */
const TIPOS_COM_BASE = new Set(["column", "cylinder", "area", "areaspline"]);
function ajustarPosicao(opcoes, spec) {
  const pct = Math.max(-60, Math.min(60, Number(spec?.eixo?.posicao) || 0));
  if (!pct || SEM_AMPLITUDE.has(spec?.modelo)) return;
  if (opcoes.chart?.inverted || opcoes.chart?.type === "bar") return;
  const tipoPadrao = opcoes.chart?.type;
  const temBase = (i) =>
    (opcoes.series || []).some(
      (s) => (typeof s.yAxis === "number" ? s.yAxis : 0) === i && TIPOS_COM_BASE.has(s?.type || tipoPadrao)
    );
  const mover = (e, i) =>
    temBase(i)
      ? { ...e, top: `${-pct}%`, height: `${100 + pct}%` } // base presa embaixo
      : { ...e, top: `${-pct}%`, height: "100%" }; // translada inteiro
  opcoes.yAxis = Array.isArray(opcoes.yAxis) ? opcoes.yAxis.map(mover) : mover(opcoes.yAxis || {}, 0);
}

/** Constrói as opções do Highcharts para um spec, seja qual for a família. */
export function construirOpcoes(spec) {
  const modelo = MODELOS_POR_ID[spec?.modelo];
  if (!modelo) throw new Error(`Modelo de gráfico desconhecido: ${spec?.modelo}`);
  const opcoes = aplicarAreaSegura(modelo.build(spec), spec);
  ajustarCaixaDeValor(opcoes, spec);
  ajustarEixoCategorias(opcoes, spec);
  ajustarAmplitude(opcoes, spec);
  ajustarPosicao(opcoes, spec);

  // Tudo explícito no eixo de valores: chart.update() mescla as opções, e
  // uma chave ausente mantém o valor anterior (voltar ao automático
  // deixava o eixo preso ao último ajuste). Os padrões são os do Highcharts.
  const explicito = (e) => ({
    startOnTick: true,
    endOnTick: true,
    minPadding: 0.05,
    maxPadding: 0.05,
    // posição: sem ajuste, o eixo ocupa a área inteira (padrão do Highcharts)
    top: "0%",
    height: "100%",
    ...e,
    min: e?.min ?? null,
    max: e?.max ?? null,
  });
  opcoes.yAxis = Array.isArray(opcoes.yAxis) ? opcoes.yAxis.map(explicito) : explicito(opcoes.yAxis);

  // A observação é desenhada no rodapé da imagem, fora da área de plotagem.
  // A folga é somada aqui, e não em cada família, porque cada uma define a
  // própria margem inferior — fazer isso lá dentro exigiria repetir a mesma
  // conta em oito arquivos e a próxima família nasceria sem ela.
  if (spec?.extras?.rodape) {
    opcoes.chart = {
      ...opcoes.chart,
      marginBottom: (opcoes.chart?.marginBottom ?? 0) + ESPACO_RODAPE,
    };
  }

  // useHTML não sobrevive à exportação: o sanitizeSVG do Highcharts corta
  // tudo que vira <foreignObject>. Melhor estourar aqui, na construção,
  // do que descobrir depois que a imagem publicada saiu sem o texto.
  if (typeof process === "undefined" || process.env?.NODE_ENV !== "production") {
    const procurar = (no, caminho = "") => {
      if (!no || typeof no !== "object") return;
      if (Array.isArray(no)) return no.forEach((n, i) => procurar(n, `${caminho}[${i}]`));
      Object.entries(no).forEach(([chave, valor]) => {
        if (chave === "useHTML" && valor === true) {
          throw new Error(`useHTML:true em ${caminho} — não sobrevive à exportação.`);
        }
        if (chave !== "rbSpec") procurar(valor, caminho ? `${caminho}.${chave}` : chave);
      });
    };
    procurar(opcoes);
  }

  return opcoes;
}

export default MODELOS;
